import { useState, useEffect, useRef } from 'react';
import { BufferGeometry, Matrix4 } from 'three';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { MeshBVH } from 'three-mesh-bvh';
import type { ColoredBlob } from '@/contexts/BlobContext';

export type CollisionPair = {
  colorA: [number, number, number, number];
  colorB: [number, number, number, number];
};

export type CollisionResult = {
  intersectingPairs: CollisionPair[];
};

/**
 * Checks for triangle-level intersections between all pairs of separate bodies
 * using BVH-accelerated geometry intersection tests (three-mesh-bvh).
 *
 * Only runs when there are 2+ exportable bodies (non-ghost, non-excluded blobs).
 * Geometries are parsed fresh for each check and disposed afterwards so the
 * render pipeline is not affected.
 *
 * All geometries are in the same local coordinate space (OpenSCAD world space),
 * so no world transform is needed — an identity Matrix4 is used for the
 * geometryToBvh argument.
 */
export function useCollisionDetection(coloredBlobs: ColoredBlob[]) {
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<CollisionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Use a generation counter to cancel in-flight checks when blobs change.
  const generationRef = useRef(0);

  useEffect(() => {
    // Only the blobs that will actually be exported are relevant.
    const exportableBlobs = coloredBlobs.filter(
      (cb) => !cb.isGhost && !cb.excludedFromExport,
    );

    // Collision detection only makes sense with 2+ separate bodies.
    if (exportableBlobs.length < 2) {
      setResult(null);
      setError(null);
      setIsChecking(false);
      return;
    }

    const thisGeneration = ++generationRef.current;
    setIsChecking(true);
    setError(null);

    const IDENTITY = new Matrix4();

    async function runCheck() {
      const geometries: BufferGeometry[] = [];

      try {
        // Parse all blobs into BufferGeometry objects.
        const loader = new STLLoader();
        for (const cb of exportableBlobs) {
          const buffer = await cb.blob.arrayBuffer();
          if (generationRef.current !== thisGeneration) return;
          const geom = loader.parse(buffer);
          geometries.push(geom);
        }

        if (generationRef.current !== thisGeneration) return;

        // Build BVH for every geometry. This modifies geom.boundsTree in place
        // and rearranges the index buffer (acceptable since these are throw-away
        // geometries used only for this check).
        //
        // We keep BVH instances in a parallel array rather than assigning to
        // geom.boundsTree, which avoids a type conflict between our
        // three-mesh-bvh and the copy bundled inside @react-three/drei.
        const bvhs: MeshBVH[] = geometries.map((geom) => new MeshBVH(geom));

        if (generationRef.current !== thisGeneration) return;

        // Check every ordered pair (i, j) where i < j.
        const intersectingPairs: CollisionPair[] = [];

        for (let i = 0; i < geometries.length; i++) {
          for (let j = i + 1; j < geometries.length; j++) {
            const bvhA = bvhs[i];
            const geomB = geometries[j];

            // geometryToBvh: transform of geomB in the local frame of bvhA.
            // Since both geometries are in OpenSCAD world space with no
            // additional object transforms, this is an identity matrix.
            const intersects = bvhA.intersectsGeometry(geomB, IDENTITY);

            if (intersects) {
              intersectingPairs.push({
                colorA: exportableBlobs[i].color,
                colorB: exportableBlobs[j].color,
              });
            }
          }
        }

        if (generationRef.current !== thisGeneration) return;

        setResult({ intersectingPairs });
      } catch (err) {
        if (generationRef.current !== thisGeneration) return;
        setError(
          err instanceof Error ? err.message : 'Collision check failed.',
        );
      } finally {
        // Dispose all geometries allocated for this check.
        for (const geom of geometries) {
          geom.dispose();
        }
        if (generationRef.current === thisGeneration) {
          setIsChecking(false);
        }
      }
    }

    runCheck();
  }, [coloredBlobs]);

  return { isChecking, result, error };
}
